import React from 'react';
import { Box } from '@mui/material';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';

export default function Tabs({ value, onChange, tabs = [], ariaLabel = 'Tabs', sx = {} }) {
  const { semantic, radiusPx, typography } = useFeatureTheme();

  const handleKeyDown = (event, tabValue, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onChange?.(tabValue);
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (index + direction + tabs.length) % tabs.length;
      onChange?.(tabs[nextIndex].value);
    }
  };

  return (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        gap: '4px',
        p: '4px',
        borderRadius: radiusPx.lg,
        bgcolor: semantic.surfaces.surfaceMuted,
        ...sx,
      }}
    >
      {tabs.map((tab, index) => {
        const isActive = value === tab.value;
        return (
          <Box
            key={tab.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange?.(tab.value)}
            onKeyDown={(event) => handleKeyDown(event, tab.value, index)}
            sx={{
              flex: 1,
              border: 'none',
              cursor: 'pointer',
              borderRadius: radiusPx.md,
              px: 1.5,
              py: 1,
              minHeight: 34,
              bgcolor: isActive ? semantic.surfaces.surface : 'transparent',
              color: isActive ? semantic.interactive.primary : semantic.text.secondary,
              fontSize: typography.fontSizeSm,
              fontWeight: isActive ? typography.fontWeightSemibold : typography.fontWeightMedium,
              boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              transition: 'all 160ms ease',
              '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 3px ${semantic.borders.focusRing}`,
              },
            }}
          >
            {tab.label}
            {typeof tab.badge === 'number' || typeof tab.badge === 'string' ? (
              <Box component="span" sx={{ ml: 0.75, opacity: 0.9 }}>
                {tab.badge}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
